import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FloatingSave } from 'corso-admin-ui';
import { CrewMerchantUi, isEnum, StoreProductStatus } from 'corso-types';
import { PropsWithChildren } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import api from '~/api';
import { useProduct } from '~/features/products/ProductProvider';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { UseFormTypes } from '~/types';
import { isErrorMessage } from '~/utils/errorResponse';

// uploads store the sheet's casing verbatim, so a status only matches the enum once lowercased
function toFormValues(product: CrewMerchantUi.StoreProductDetail) {
  const status = product.status?.toLowerCase();

  return {
    title: product.title,
    status: isEnum(status, StoreProductStatus)
      ? status
      : StoreProductStatus.active,
    productType: product.productType ?? null,
    vendor: product.vendor ?? null,
    tags: product.tags ?? [],
  } satisfies CrewMerchantUi.StoreProductUpdate;
}

export function useInvalidateProduct() {
  const queryClient = useQueryClient();

  return () =>
    Promise.all(
      [['product'], ['search']].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
}

function useProductForm(product: CrewMerchantUi.StoreProductDetail) {
  return useForm({
    resolver: zodResolver(CrewMerchantUi.storeProductUpdateSchema),
    values: toFormValues(product),
  });
}

export function useProductFormContext() {
  type FormTypes = UseFormTypes<typeof useProductForm>;
  return useFormContext<
    FormTypes['FieldValues'],
    FormTypes['Context'],
    FormTypes['TransformedValues']
  >();
}

export function ProductForm({ children }: PropsWithChildren) {
  const product = useProduct();
  const storeId = useStoreId();
  const toast = useToast();
  const invalidateProduct = useInvalidateProduct();

  const methods = useProductForm(product);

  const { mutateAsync: saveProduct } = useMutation({
    mutationFn: (body: CrewMerchantUi.StoreProductUpdate) =>
      api.store(storeId).product.update(`${product.id}`, body),
    onSuccess: invalidateProduct,
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  const {
    formState: { isDirty, isSubmitting },
  } = methods;

  return (
    <FormProvider {...methods}>
      {product.isOffPlatform && (
        <FloatingSave
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          onSubmit={() => {
            methods
              .handleSubmit((values) => saveProduct(values))()
              .catch(console.error);
          }}
          onReset={() => methods.reset()}
        />
      )}
      {children}
    </FormProvider>
  );
}
